import { useNavigate } from "react-router";
import { formatDistanceToNow } from "date-fns";
import { cn } from "../../lib/utils";
import { useMarkNotificationRead } from "../../hooks/useNotifications";
import type { Notification } from "../../types/notification";

interface NotificationItemProps {
  notification: Notification;
  onClose: () => void;
}

export function NotificationItem({
  notification,
  onClose,
}: NotificationItemProps) {
  const navigate = useNavigate();
  const { mutate: markAsRead } = useMarkNotificationRead();

  function handleClick() {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    if (notification.action_url) {
      navigate(notification.action_url);
    }
    onClose();
  }

  const timeAgo = formatDistanceToNow(new Date(notification.createdAt), {
    addSuffix: true,
  });

  return (
    <button
      type="button"
      className={cn(
        "flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40",
        !notification.is_read && "bg-primary-light/60"
      )}
      onClick={handleClick}
    >
      {/* Unread dot */}
      <div className="mt-1.5 shrink-0">
        <div
          className={cn(
            "size-2 rounded-full",
            notification.is_read ? "bg-transparent" : "bg-primary"
          )}
          aria-hidden="true"
        />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "truncate text-[13px]",
            notification.is_read
              ? "font-medium text-ink-soft"
              : "font-semibold text-ink"
          )}
        >
          {notification.title}
        </p>
        <p className="mt-0.5 line-clamp-2 text-xs text-ink-muted">
          {notification.body}
        </p>
        <p className="mt-1 text-[11px] text-ink-faint">{timeAgo}</p>
      </div>
    </button>
  );
}
